"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.QrCode = QrCode;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const uqr_1 = require("uqr");
const cn_1 = require("../lib/cn");
const icon_1 = require("./icon");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ A QR code: one string, rendered as modules somebody can point a phone at. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Domain-agnostic like everything else here: it takes a `value` and knows nothing about
 * authenticators, `otpauth://` or enrolment. The first caller is the Control Panel's authenticator
 * setup; the product's own two-factor dialog is the second one waiting for it
 * (`apps/web/src/screens/account/security-section.tsx` shows a bare secret today).
 *
 * ⚠️⚠️ **THE ENCODER IS A DEPENDENCY (`uqr`, MIT, ZERO DEPS), AND THAT WAS A DECISION.** QR
 * encoding is Reed-Solomon over GF(256) plus per-version ECC block tables plus eight mask patterns
 * scored against four penalty rules — several hundred lines of standard, most of it table data. The
 * argument against writing it here is not the effort: **a subtly wrong encoder produces a picture
 * that looks exactly like a QR code and does not scan.** That is this project's signature failure
 * (it compiles, it loads, it does not work) in the one place where the person hitting it is locked
 * out of the console. `decisions.md` R-9 refused an i18n library because we needed 5% of it; here we
 * need 100% of a 27 KB file with no dependencies of its own.
 *
 * ⚠️ **WHAT IS NOT DELEGATED: the drawing.** `uqr` can emit an SVG string, and using it would mean
 * `dangerouslySetInnerHTML` in a security console plus a fixed `white`/`black` palette. So this
 * takes the module matrix from `encode()` and builds the geometry itself — no injected markup, and
 * the colours are tokens.
 */
/**
 * The module matrix as one SVG path: a horizontal run of dark modules becomes one subpath.
 *
 * ⚠️ **One `<path>`, not one `<rect>` per module, and the difference is 2,000 DOM nodes.** A
 * version-9 code is 61 × 61 with the quiet zone — around 1,800 dark modules. React reconciling that
 * many elements on every keystroke in the field below it is a visible stall; a single `d` string is
 * one attribute.
 *
 * ⚠️ Coordinates are in MODULE units, and the `viewBox` does the scaling. Fractional CSS pixels per
 * module are therefore the browser's problem rather than ours, which is what `crispEdges` is for.
 */
function modulePath(matrix) {
    const parts = [];
    matrix.forEach((row, y) => {
        let run = 0;
        /* `<=` deliberately: the extra iteration reads past the end, where the value is `undefined`,
         * and flushes a run that reaches the last column.
         *
         * ⚠️ **Unreachable while the quiet zone exists, and kept anyway — measured, not assumed.**
         * With `border: 4` the last column is always light, so no run ever ends at the edge; changing
         * this to `<` passes the whole suite today. It is here so that making the border configurable
         * does not silently drop the rightmost modules of every row, which renders a plausible-looking
         * code that does not scan. */
        for (let x = 0; x <= row.length; x += 1) {
            if (row[x] === true) {
                run += 1;
                continue;
            }
            if (run > 0) {
                parts.push(`M${String(x - run)} ${String(y)}h${String(run)}v1h-${String(run)}z`);
                run = 0;
            }
        }
    });
    return parts.join('');
}
/**
 * ⚠️⚠️ **THE PANEL PINS ITSELF TO THE LIGHT PALETTE WITH `data-theme="light"`, AND THAT IS THE
 * WHOLE REASON IT IS A `div` WITH AN ATTRIBUTE ON IT.** A QR code is specified dark-on-light. An
 * inverted one is decoded by iOS's camera and refused by a good number of authenticator apps, so a
 * code that follows the theme is a code that works for some readers and not others — and the
 * failure lands on the person setting up two-factor, who has no way to tell whose fault it is.
 *
 * `tokens.css` declares the light palette as `:root, [data-theme='light']`, so the attribute
 * re-declares every custom property for this subtree: `--card` is `oklch(1 0 0)` — pure white — and
 * `--card-foreground` near-black, in all eight themes. No colour literal, no lint exemption, and
 * the mechanism is the design system's own. `AuthFrame` flags the same hazard from the other side:
 * the auth pages are light by construction *today*, and a theme-at-boot restore would make this the
 * only element on them that must not move.
 *
 * ⚠️ The dark modules are `currentColor`, inherited from `text-card-foreground` on the panel, so
 * there is one place that decides the pair.
 */
function QrCode({ value, size = 176, label, className }) {
    /**
     * ⚠️ **Memoised on the value, because the caller re-renders on every keystroke.** The
     * authenticator screen has a six-digit field under this panel: without the memo the code is
     * encoded seven times for one setup, and a version-9 encode is Reed-Solomon over ~200 bytes plus
     * eight mask patterns scored — not free.
     *
     * ⚠️ **`border: 4` is the SPEC's quiet zone, not a margin.** ISO/IEC 18004 requires four light
     * modules on every side; `uqr` defaults to one. A code drawn tight to a bordered panel scans
     * unreliably, and it is the kind of thing that works on the phone of whoever built it.
     */
    const encoded = (0, react_1.useMemo)(() => {
        try {
            return (0, uqr_1.encode)(value, { border: 4 });
        }
        catch {
            /* `RangeError: Data too long` past version 40 (2,953 bytes) is the only documented throw.
             * Returning null renders the fallback below rather than taking the screen down: the caller
             * shows the encoded string as text too, so a missing picture is an inconvenience and an
             * exception here would be a blank page. */
            return null;
        }
    }, [value]);
    if (encoded === null) {
        /* ⚠️ The mockup's own placeholder, reused as the failure state: `docs/mockup/markup.html` draws
         * this box with a 64px `qr-code` glyph in it, because a prototype with mock services has
         * nothing to encode. Here it means "there is no picture, use the text". */
        return ((0, jsx_runtime_1.jsx)("span", { "data-qr-unavailable": "", className: (0, cn_1.cn)('bg-muted text-muted-foreground grid shrink-0 place-items-center rounded-lg border', className), style: { width: size, height: size }, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "qr-code", size: 64 }) }));
    }
    return ((0, jsx_runtime_1.jsx)("div", { "data-theme": "light", className: (0, cn_1.cn)('bg-card text-card-foreground shrink-0 overflow-hidden rounded-lg border', className), style: { width: size, height: size }, children: (0, jsx_runtime_1.jsx)("svg", { role: "img", "aria-label": label, viewBox: `0 0 ${String(encoded.size)} ${String(encoded.size)}`, className: "block h-full w-full", 
            /* ⚠️ Without this the browser antialiases every module edge, which at three CSS pixels per
             * module blurs the grid a decoder is trying to find. */
            shapeRendering: "crispEdges", children: (0, jsx_runtime_1.jsx)("path", { d: modulePath(encoded.data), fill: "currentColor" }) }) }));
}
//# sourceMappingURL=qr-code.js.map